import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { ORIGIN_META, ORIGIN_ORDER } from "@/lib/provenance";
import { OriginBadge } from "@/components/common/OriginBadge";
import { toast } from "sonner";
import { RefreshCw, Trash2, ShieldCheck, Loader2, Table2 } from "lucide-react";
import { ProvenanceRecordsDialog } from "@/components/provenance/ProvenanceRecordsDialog";

const MODULE_LABELS = {
  finance: "Finance", operations: "Operations", crm: "CRM & Sales",
  hseq: "HSEQ", hr: "HR", capital: "Capital", fleet: "Fleet",
  sustainability: "Sustainability",
};

function OriginBar({ counts, total }) {
  if (!total) return <div className="h-2 rounded-full bg-slate-100" />;
  return (
    <div className="h-2 rounded-full overflow-hidden bg-slate-100 flex" title={`${total} records`}>
      {ORIGIN_ORDER.map((o) => {
        const n = counts[o] || 0;
        if (!n) return null;
        return <div key={o} className={ORIGIN_META[o].dot} style={{ width: `${(n / total) * 100}%` }} title={`${ORIGIN_META[o].label}: ${n}`} />;
      })}
    </div>
  );
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " " +
         d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function RecentRetags({ log }) {
  if (!log?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="provenance-retag-log">
      <h2 className="text-sm font-semibold text-slate-900 mb-3">Recently re-tagged</h2>
      <div className="divide-y divide-slate-100">
        {log.map((e) => {
          const filterText = e.filters && Object.keys(e.filters).length
            ? Object.entries(e.filters).map(([k, v]) => `${k}="${v}"`).join(", ") : null;
          return (
            <div key={e.id} data-testid={`retag-log-${e.id}`} className="py-2 flex items-center gap-3 text-xs flex-wrap">
              <span className="font-medium text-slate-800 tabular-nums">{(e.count || 0).toLocaleString()}×</span>
              <span className="text-slate-700">{e.dataset_label}</span>
              <span className="text-slate-400">→</span>
              <OriginBadge origin={e.origin} size="xs" />
              {e.scope === "filtered" && filterText && (
                <span className="text-[11px] text-slate-400">matching {filterText}</span>
              )}
              {e.scope === "selection" && <span className="text-[11px] text-slate-400">selected rows</span>}
              <span className="ml-auto text-slate-400">{e.actor} · {fmtWhen(e.at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DatasetRow({ d, onChanged }) {
  const [origin, setOrigin] = useState("synced");
  const [busy, setBusy] = useState(false);
  const [showRecords, setShowRecords] = useState(false);
  const c = d.counts;

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/provenance/verify", { dataset_key: d.key, origin, only_unverified: true });
      toast.success(`${d.label}: ${r.data.updated} unverified record(s) marked as ${origin}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
    finally { setBusy(false); }
  };

  const purge = async () => {
    setBusy(true);
    try {
      const dry = await api.post("/provenance/purge-sample", { dataset_key: d.key, confirm: "" });
      const n = dry.data.would_delete || 0;
      if (!n) { toast.info(`${d.label}: no sample records to purge`); return; }
      if (!window.confirm(`Permanently delete ${n} SAMPLE record(s) from "${d.label}"? This cannot be undone.`)) return;
      const res = await api.post("/provenance/purge-sample", { dataset_key: d.key, confirm: "PURGE" });
      toast.success(`${d.label}: deleted ${res.data.deleted} sample record(s)`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`provenance-dataset-${d.key}`} className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
            {d.label}
            {d.has_sample && <OriginBadge origin="sample" size="xs" />}
            {!d.has_sample && d.has_unverified && <OriginBadge origin="unverified" size="xs" />}
          </div>
          <div className="text-[11px] text-slate-400 font-mono">{d.collection} · {d.total.toLocaleString()} records</div>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          {ORIGIN_ORDER.map((o) => c[o] ? (
            <span key={o} className="inline-flex items-center gap-1 text-slate-600">
              <span className={`w-2 h-2 rounded-full ${ORIGIN_META[o].dot}`} />{ORIGIN_META[o].label} {c[o].toLocaleString()}
            </span>
          ) : null)}
        </div>
      </div>
      <div className="mt-2"><OriginBar counts={c} total={d.total} /></div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button data-testid={`provenance-view-records-${d.key}`} disabled={!d.total} onClick={() => setShowRecords(true)}
          className="h-7 inline-flex items-center gap-1 text-xs px-2.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          <Table2 className="w-3 h-3" /> View records
        </button>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <span className="text-[11px] text-slate-500">Mark unverified as</span>
        <select data-testid={`provenance-verify-select-${d.key}`} value={origin} onChange={(e) => setOrigin(e.target.value)}
          className="h-7 text-xs border border-slate-200 rounded-md px-2 bg-white">
          {["synced", "imported", "manual", "sample"].map((o) => <option key={o} value={o}>{ORIGIN_META[o].label}</option>)}
        </select>
        <button data-testid={`provenance-verify-apply-${d.key}`} disabled={busy || !d.has_unverified} onClick={verify}
          className="h-7 inline-flex items-center gap-1 text-xs px-2.5 rounded-md bg-[#00334C] text-white disabled:opacity-40">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Verify
        </button>
        <button data-testid={`provenance-purge-${d.key}`} disabled={busy || !d.has_sample} onClick={purge}
          className="h-7 inline-flex items-center gap-1 text-xs px-2.5 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-40">
          <Trash2 className="w-3 h-3" /> Purge sample
        </button>
      </div>
      <ProvenanceRecordsDialog dataset={d} open={showRecords} onClose={() => setShowRecords(false)} onChanged={onChanged} />
    </div>
  );
}

export default function DataProvenancePage() {
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  const loadLog = () => api.get("/provenance/retag-log?limit=30").then((r) => setLog(r.data || [])).catch(() => {});
  const load = () => {
    loadLog();
    return api.get("/provenance/summary").then((r) => setSum(r.data)).catch((e) => {
      if (e.response?.status === 403) setSum({ forbidden: true });
    });
  };
  useEffect(() => { load(); }, []);

  const reclassifyAll = async () => {
    setBusy(true);
    try { await api.post("/provenance/reclassify"); toast.success("Reclassified all datasets"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reclassify failed"); }
    finally { setBusy(false); }
  };

  const verifyAll = async () => {
    if (!window.confirm("Mark ALL currently-unverified records across every dataset as Imported (real data)? Human-verified.")) return;
    setBusy(true);
    try { const r = await api.post("/provenance/verify-all?origin=imported"); toast.success(`${r.data.updated} record(s) verified as Imported`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verify-all failed"); }
    finally { setBusy(false); }
  };

  if (sum?.forbidden) {
    return <AppLayout title="Data Provenance"><div className="text-sm text-slate-500">Admin access required.</div></AppLayout>;
  }

  const g = sum?.global;
  const modules = sum ? Object.entries(sum.modules).filter(([, m]) => m.datasets.length) : [];

  return (
    <AppLayout title="Data Provenance" subtitle="Know what's real vs sample data across the hub"
      actions={
        <div className="flex items-center gap-2">
          <button data-testid="provenance-verify-all" onClick={verifyAll} disabled={busy}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-[#00334C] text-white hover:opacity-90 disabled:opacity-60">
            <ShieldCheck className="w-4 h-4" /> Verify all as Imported
          </button>
          <button data-testid="provenance-reclassify-all" onClick={reclassifyAll} disabled={busy}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Re-scan
          </button>
        </div>
      }>
      {!sum ? (
        <div className="h-40 shimmer rounded-lg" />
      ) : (
        <div className="space-y-6" data-testid="data-provenance-page">
          {/* Global rollup */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-900">Across the whole hub</h2>
              <span className="text-xs text-slate-400">{g.total.toLocaleString()} decision records</span>
            </div>
            <OriginBar counts={g.counts} total={g.total} />
            <div className="mt-3 flex items-center gap-4 flex-wrap text-xs">
              {ORIGIN_ORDER.map((o) => (
                <div key={o} className="inline-flex items-center gap-1.5">
                  <OriginBadge origin={o} size="xs" />
                  <span className="text-slate-700 font-medium tabular-nums">{(g.counts[o] || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
            {(g.counts.sample > 0 || g.counts.unverified > 0) && (
              <p className="mt-3 text-xs text-slate-500">
                {g.counts.sample > 0 && <span className="text-rose-600 font-medium">{g.counts.sample.toLocaleString()} sample record(s) still in the hub. </span>}
                {g.counts.unverified > 0 && <span className="text-amber-700">{g.counts.unverified.toLocaleString()} record(s) need their origin verified below.</span>}
              </p>
            )}
          </div>

          {/* Recently re-tagged audit trail */}
          <RecentRetags log={log} />

          {/* Per-module */}
          {modules.map(([key, m]) => (
            <div key={key} data-testid={`provenance-module-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">{MODULE_LABELS[key] || key}</h3>
                <div className="w-40"><OriginBar counts={m.counts} total={m.total} /></div>
              </div>
              <div className="space-y-2">
                {m.datasets.map((d) => <DatasetRow key={d.key} d={d} onChanged={load} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
